import { Trophy, Award, Medal } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface PendingRankingTableProps {
  title?: string;
  modalidad?: 'singles' | 'doubles';
  type?: 'round-robin' | 'sumatoria-juegos' | 'escalera';
  participantCount?: number;
  className?: string;
  onShowBrackets?: () => void;
}

const PendingRankingTable = ({ 
  title = "Tabla de Posiciones",
  modalidad = 'doubles',
  type = 'round-robin',
  participantCount = 4,
  className = "",
  onShowBrackets
}: PendingRankingTableProps) => {
  
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Trophy className="h-4 w-4 text-yellow-600" />;
      case 2:
        return <Medal className="h-4 w-4 text-gray-500" />;
      case 3:
        return <Award className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getTableHeaders = () => {
    switch (type) {
      case 'sumatoria-juegos':
        return ['Pos', 'Participante', 'Juegos', 'Diferencia'];
      case 'escalera':
        return ['Pos', 'Participante', 'Puntos', 'Partidos'];
      default:
        return ['Pos', 'Participante', 'V', 'D', 'JF', 'JC', 'Pts'];
    }
  };

  const generatePendingRows = () => {
    const rows = [];
    for (let i = 1; i <= participantCount; i++) {
      rows.push(
        <TableRow key={i} className="hover:bg-slate-50">
          <TableCell className="font-medium">
            <div className="flex items-center space-x-2">
              <span className="text-slate-400">{i}</span>
              {getPositionIcon(i)}
            </div>
          </TableCell>
          <TableCell className="text-slate-400">
            <div className="flex items-center gap-2">
              <div className="w-24 h-4 bg-slate-200 rounded animate-pulse"></div>
              {i <= 3 && (
                <Badge variant="outline" className="text-xs text-slate-400 border-slate-300">
                  TBD
                </Badge>
              )}
            </div>
          </TableCell>
          {type === 'sumatoria-juegos' ? (
            <>
              <TableCell className="text-center text-slate-400">--</TableCell>
              <TableCell className="text-center text-slate-400">--</TableCell>
            </>
          ) : type === 'escalera' ? (
            <>
              <TableCell className="text-center text-slate-400">--</TableCell>
              <TableCell className="text-center text-slate-400">--</TableCell>
            </>
          ) : (
            <>
              <TableCell className="text-center text-slate-400">--</TableCell>
              <TableCell className="text-center text-slate-400">--</TableCell>
              <TableCell className="text-center text-slate-400">--</TableCell>
              <TableCell className="text-center text-slate-400">--</TableCell>
              <TableCell className="text-center font-bold text-slate-400">--</TableCell>
            </>
          )}
        </TableRow>
      );
    }
    return rows;
  };

  return (
    <Card className={`bg-gradient-to-br from-slate-50 to-green-50 border-slate-200 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-slate-700">{title}</span>
            <Badge 
              variant="outline" 
              className="border-slate-300 text-slate-600 bg-white/50"
            >
              Por definir
            </Badge>
          </div>
          {onShowBrackets && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onShowBrackets}
              className="text-slate-600 border-slate-300 hover:bg-slate-50"
            >
              Ver Brackets
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-slate-500">
              Las posiciones se actualizarán conforme se jueguen los partidos
            </p>
          </div>
          
          <Table>
            <TableHeader>
              <TableRow>
                {getTableHeaders().map((header, index) => (
                  <TableHead 
                    key={index} 
                    className={index === 0 ? "w-16" : index === 1 ? "" : "text-center"}
                  >
                    {header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {generatePendingRows()}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingRankingTable;